import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { COMPANY } from "../data/company";
import SEO from "../components/SEO";
import ContactForm from "../components/ContactForm";
import ReviewsSlider from "../components/ReviewsSlider";
import TerraceHeroVideo from "../components/TerraceHeroVideo";
import { imageUrl } from "../lib/images";

export default function Home() {
  const [galleries, setGalleries] = useState([]);

  // Fetch CMS galleries for showcase
  useEffect(() => {
    let ignore = false;
    async function loadGalleries() {
      try {
        const res = await fetch("/_api/public/content/galleries");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && !ignore) {
            setGalleries(data);
          }
        }
      } catch (err) {
        // fail silently
      }
    }
    loadGalleries();
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <SEO
        title=""
        description={`${COMPANY.name} i ${COMPANY.city} er specialister i døre & vinduer, opførelse af træterrasser samt indendørs renovering af lofter og vægge. Kontakt Andreas på ${COMPANY.phone}.`}
        path="/"
        image="/_assets/hero-toemrer-arbejde.jpg"
      />

      {/* Hero Section */}
      <section className="relative bg-[#111111] text-white overflow-hidden border-b border-neutral-800">
        {/* Background Image with craft overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl("/_assets/hero-toemrer-arbejde.jpg", "width=1920,quality=80,format=auto")}
            alt="Tømrerarbejde udført af Tømrermester Andreas Guldmann ApS"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0">
            <TerraceHeroVideo />
            <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/90 to-[#111111]/60" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <div className="max-w-3xl">
            {/* Tagline badge */}
            <div className="inline-flex items-center gap-2 text-[#A78E51] text-xs font-bold uppercase tracking-wider mb-6">
              <span className="w-2 h-2 rounded-full bg-[#A78E51]"></span>
              <span>Tømrermester i Skårup Fyn</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
              Kvalitetsbevidst tømrerarbejde med solidt håndværk
            </h1>

            {/* Subheading / Presentation */}
            <p className="mt-6 text-base sm:text-lg text-neutral-300 leading-relaxed max-w-2xl">
              Velkommen til <strong className="text-white">{COMPANY.name}</strong>. Vi elsker at lave døre og vinduer og varetager desuden opførelse af lækre træterrasser og uderum samt indendørs renovering af lofter og vægge med kompromisløst fokus på godt håndværk.
            </p>

            {/* CTAs */}
            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-md bg-[#A78E51] text-white font-extrabold text-sm hover:bg-[#937B43] shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#A78E51]"
              >
                <span>Kontakt Andreas</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>

              <Link
                to="/galleri"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-md bg-white/10 hover:bg-white/15 text-white font-bold text-sm border border-neutral-600 hover:border-neutral-400 transition-all focus:outline-none focus:ring-2 focus:ring-white"
              >
                <i className="fa-solid fa-images text-[#A78E51] text-xs" aria-hidden="true" />
                <span>Se vores arbejde</span>
              </Link>

              <a
                href={COMPANY.phoneLink}
                className="inline-flex items-center gap-2 px-4 py-3.5 text-white hover:text-[#A78E51] font-bold text-sm transition-colors whitespace-nowrap"
              >
                <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center text-[#A78E51]">
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                </div>
                <span>Tlf. {COMPANY.phone}</span>
              </a>
            </div>


          </div>
        </div>
      </section>

      {/* De tre primære ydelser sektion */}
      <section className="py-16 sm:py-24 bg-[#FAFAF8]" aria-label="Primære ydelser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header area: Left-aligned eyebrow, large display heading, body text, balanced by right-aligned CTA link */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 pb-6 sm:pb-8 border-b border-neutral-200/90 mb-10 sm:mb-14">
            <div className="max-w-2xl">
              <span className="text-[#A78E51] font-bold text-xs uppercase tracking-[0.2em] block mb-3">
                Hvad vi udfører
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111111] tracking-tight">
                Tre centrale <span className="text-[#A78E51]">arbejdsområder</span>
              </h2>
              <p className="mt-3.5 sm:mt-4 text-neutral-600 text-base sm:text-lg leading-relaxed">
                Vi løser både store og mindre tømreropgaver med grundighed og sans for detaljen.
              </p>
            </div>
            <div className="pt-2 lg:pt-0">
              <Link
                to="/ydelser"
                className="inline-flex items-center gap-2.5 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#111111] hover:text-[#A78E51] transition-colors group"
              >
                <span>Se alle ydelser</span>
                <i
                  className="fa-solid fa-arrow-right text-xs text-[#A78E51] transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>

          {/* 3-column grid of tall image cards with sharp corners and dark bottom-gradient overlays */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-6 lg:gap-8">
            {COMPANY.services.map((svc) => {
              const serviceLabels = {
                "doere-og-vinduer": "Vores store passion",
                "traeterrasser-og-udendoersarealer": "Uderum & terrasser",
                "indendoers-renovering": "Lofter & vægge",
              };
              const cardLabel = serviceLabels[svc.slug] || "Tømrerarbejde";

              return (
                <Link
                  key={svc.slug}
                  to={svc.path}
                  className="group relative block aspect-[3/4] sm:aspect-[4/5] md:aspect-[3/4] lg:aspect-[3/4] min-h-[460px] sm:min-h-[480px] lg:min-h-[520px] rounded-none overflow-hidden bg-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:ring-offset-2"
                  aria-label={`${svc.title} - Læs mere`}
                >
                  {/* Full-bleed background image with sharp corners */}
                  <img
                    src={imageUrl(svc.image, "width=1000,quality=85,format=auto")}
                    alt={svc.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />

                  {/* Dark bottom-gradient overlay */}
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/10 transition-opacity duration-300"
                    aria-hidden="true"
                  />

                  {/* Content framed over the gradient overlay */}
                  <div className="relative h-full flex flex-col justify-end p-6 sm:p-7 lg:p-8 text-white z-10">
                    {/* Card label in accent color */}
                    <span className="text-[#A78E51] text-xs font-bold uppercase tracking-[0.2em] block mb-2">
                      {cardLabel}
                    </span>

                    {/* Heading */}
                    <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2.5 group-hover:text-neutral-100 transition-colors">
                      {svc.title}
                    </h3>

                    {/* Body text in light inverse color */}
                    <p className="text-neutral-300 text-sm leading-relaxed mb-6 line-clamp-3">
                      {svc.shortDesc}
                    </p>

                    {/* Action link and square outline icon button */}
                    <div className="pt-4 border-t border-white/20 flex items-center justify-between gap-4">
                      <span className="text-xs font-bold uppercase tracking-[0.16em] text-white/90 group-hover:text-[#A78E51] transition-colors">
                        Læs mere
                      </span>
                      <div
                        className="w-10 h-10 rounded-none border border-white/40 text-white group-hover:border-[#A78E51] group-hover:text-[#A78E51] group-hover:bg-[#A78E51]/10 flex items-center justify-center transition-all shrink-0"
                        aria-hidden="true"
                      >
                        <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personlig præsentation af Andreas Guldmann */}
      <section className="py-16 sm:py-24 bg-white border-t border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Visual workshop / craft image */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-lg overflow-hidden border border-neutral-200 shadow-md">
                <img
                  src="https://assets.auxodev.dk/assets/3fc68df7-bc32-4dbe-a468-aab3c89985d1/820180811-122128316756949713-247210780206571009-n.jpg"
                  alt="Værktøj og tømrerhåndværk hos Andreas Guldmann"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

              </div>
            </div>

            {/* Text content */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-2">
                Om virksomheden
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111111] tracking-tight">
                Tømrermester Andreas Guldmann
              </h2>
              <div className="mt-4 space-y-4 text-neutral-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Hos <strong>Tømrermester Andreas Guldmann ApS</strong> er målet enkelt: at levere et solidt stykke tømrerarbejde, der holder i mange år frem. Virksomheden har base på Nyborgvej i Skårup Fyn og udfører arbejde for private boligejere og ejendomsejere.
                </p>
                <p>
                  Når du kontakter virksomheden, taler du direkte med Andreas. Det sikrer kort vej fra idé og besigtigelse til det færdige resultat, klare aftaler om tid og pris samt tryghed gennem hele byggeprocessen.
                </p>
                <p>
                  Uanset om opgaven indebærer montering af nye energieffektive døre og vinduer (vores helt store hjertebarn!), opførelse af en skræddersyet træterrasse eller fornyelse af lofter og vægge, lægges der vægt på korrekt håndværksmæssig udførelse og respekt for boligens stil.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={COMPANY.phoneLink} className="inline-flex items-center justify-center gap-2.5 rounded-md bg-[#A78E51] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#937B43] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A78E51] focus-visible:ring-offset-2">
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  Ring til Andreas
                </a>
                <Link to="/kontakt#kontaktformular" className="inline-flex items-center justify-center gap-2.5 rounded-md border border-neutral-200 bg-white px-6 py-3.5 text-sm font-bold text-[#111111] transition-colors hover:border-[#A78E51] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A78E51] focus-visible:ring-offset-2">
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  Skriv til Andreas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Anmeldelsessektion (Infinite Slider jf. sektion 8) */}
      <ReviewsSlider />

      {/* Afsluttende kontaktsektion */}
      <section className="py-16 sm:py-24 bg-white" aria-label="Kontakt sektion">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-2">
                Tag kontakt
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111111] tracking-tight">
                Få en uforpligtende snak om dit byggeprojekt
              </h2>
              <p className="mt-4 text-neutral-600 text-sm sm:text-base leading-relaxed">
                Udfyld formularen, eller giv Andreas et direkte opkald. Vi vender hurtigt tilbage med en vurdering af opgaven.
              </p>

              <div className="mt-8 space-y-4">
                <a
                  href={COMPANY.phoneLink}
                  className="flex items-center gap-4 p-4 rounded-lg bg-[#FAFAF8] border border-neutral-200 hover:border-[#A78E51] transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      Ring direkte til Andreas
                    </span>
                    <span className="text-base font-bold text-[#111111] group-hover:text-[#A78E51] transition-colors">
                      {COMPANY.phone}
                    </span>
                  </div>
                </a>

                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-4 p-4 rounded-lg bg-[#FAFAF8] border border-neutral-200 hover:border-[#A78E51] transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      Send en e-mail
                    </span>
                    <span className="text-base font-bold text-[#111111] group-hover:text-[#A78E51] transition-colors">
                      {COMPANY.email}
                    </span>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-[#FAFAF8] border border-neutral-200">
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      Virksomhedens adresse
                    </span>
                    <span className="text-sm font-bold text-[#111111] block">
                      {COMPANY.address}
                    </span>
                    <span className="text-xs text-neutral-500">
                      CVR: {COMPANY.cvr}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm backgroundClass="bg-[#FAFAF8] [&_input]:bg-white [&_select]:bg-white [&_textarea]:bg-white" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
